import React from 'react'
import { motion } from 'framer-motion'
import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js'
import { format } from 'date-fns'

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend)

const StakingRewardResult = ({ result }) => {
  if (!result) return null

  const data = result
  const { staking_details, results } = data
  const formattedDate = format(new Date(data.timestamp), 'PPpp')

  const chartData = {
    labels: ['Invested USD', 'Reward USD', 'Final Value USD'],
    datasets: [
      {
        label: 'USD Value',
        data: [results.invested_usd, results.reward_usd, results.final_value_usd],
        backgroundColor: ['#3b82f6', '#10b981', '#facc15'],
        borderRadius: 10,
        barThickness: 30
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => `$${ctx.raw.toFixed(2)}`
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: val => `$${val}`,
          color: '#fff'
        },
        grid: { color: 'rgba(255,255,255,0.1)' }
      },
      x: {
        ticks: { color: '#fff' },
        grid: { display: false }
      }
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="rounded-2xl p-6 shadow-xl  bg-slate-800/50  text-white backdrop-blur-lg border border-white/10"
    >
      <h2 className="text-2xl font-bold mb-4">Staking Reward Result</h2>
      <p className="text-sm text-gray-300 mb-6">Data reflects the most recent update as of:: {formattedDate}</p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <InfoBlock label="Exchange" value={data.exchange} />
        <InfoBlock label="Symbol" value={data.symbol} />
        <InfoBlock label="Current Price" value={`$${data.current_price.toFixed(2)}`} />
        <InfoBlock label="Staked Amount" value={`${staking_details.amount} ${data.symbol.split('/')[0]}`} />
        <InfoBlock label="APY" value={`${staking_details.apy}%`} />
        <InfoBlock label="Duration" value={`${staking_details.years} years`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <InfoBlock label="Final Amount" value={results.final_amount} />
        <InfoBlock label="Rewards (Token)" value={results.rewards} />
        <InfoBlock label="ROI %" value={`${results.roi_pct}%`} />
      </div>

      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-3">USD Breakdown</h3>
        <div className="overflow-x-auto rounded-xl">
          <table className="min-w-full text-sm text-left bg-zinc-800/50 border border-white/10 rounded-xl">
            <thead className="bg-zinc-700/50 text-gray-300">
              <tr>
                <th className="px-4 py-2">Metric</th>
                <th className="px-4 py-2">Value (USD)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-zinc-700">
                <td className="px-4 py-2">Invested</td>
                <td className="px-4 py-2">${results.invested_usd.toFixed(2)}</td>
              </tr>
              <tr className="border-t border-zinc-700">
                <td className="px-4 py-2">Reward</td>
                <td className="px-4 py-2">${results.reward_usd.toFixed(2)}</td>
              </tr>
              <tr className="border-t border-zinc-700">
                <td className="px-4 py-2 font-semibold text-yellow-300">Final Value</td>
                <td className="px-4 py-2 font-semibold text-yellow-300">${results.final_value_usd.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-2">Investment Growth Chart</h3>
        <Bar data={chartData} options={chartOptions} />
      </div>
      <p className="text-xs text-gray-300 mb-6">This is tentative data and may not reflect precise values. </p>

    </motion.div>
  )
}

const InfoBlock = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-400">{label}</p>
    <p className="font-semibold">{value}</p>
  </div>
)

export default StakingRewardResult
